import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Layers, Mountain, Gem, Palette, Target, Satellite, Sparkles, Rocket, FileDown, Loader2, History, Pencil, Trash2, Settings2, Link2, Copy, ExternalLink, Eye, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return iso; }
};

export default function CommodityExplorer() {
  const nav = useNavigate();
  const [cat, setCat] = useState(null);
  const [sel, setSel] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    if (!sel) return;
    setExporting(true);
    try {
      const res = await api.get(`/commodities/${encodeURIComponent(sel)}/onepager.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${sel.replace(/\s+/g, "_")}_Targeting_Model.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export the PDF one-pager"); }
    finally { setExporting(false); }
  };

  const [packSel, setPackSel] = useState([]);
  const [packing, setPacking] = useState(false);
  const [packOpen, setPackOpen] = useState(false);
  const [packForm, setPackForm] = useState({ title: "", client_name: "", blurb: "", client_logo: "" });
  const togglePack = (n) => setPackSel((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const rd = new FileReader();
    rd.onload = () => setPackForm((p) => ({ ...p, client_logo: rd.result }));
    rd.readAsDataURL(f);
  };
  const doBuildPack = async () => {
    if (!packSel.length) return;
    setPacking(true);
    try {
      const res = await api.post(`/commodities/proposal-pack`, { commodities: packSel, ...packForm }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(packForm.title || "SYMIN_Proposal_Pack").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Proposal pack built (${packSel.length} model${packSel.length > 1 ? "s" : ""})`);
      setPackOpen(false);
    } catch (e) { toast.error("Could not build the proposal pack"); }
    finally { setPacking(false); }
  };

  const [shareExpiry, setShareExpiry] = useState(30);
  const [shareLink, setShareLink] = useState(null);
  const [sharing, setSharing] = useState(false);
  const createShareLink = async () => {
    if (!packSel.length) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/commodities/proposal-pack/share`, { commodities: packSel, ...packForm, expires_days: shareExpiry, password: sharePw || undefined });
      const full = `${window.location.origin}${data.path}`;
      setShareLink({ url: full, expires_at: data.expires_at, protected: data.protected });
      toast.success(data.protected ? "Passcode-protected link created" : "Shareable client link created");
    } catch (e) { toast.error("Could not create the shareable link"); }
    finally { setSharing(false); }
  };
  const copyShareLink = () => {
    if (!shareLink?.url) return;
    try { navigator.clipboard.writeText(shareLink.url); toast.success("Link copied to clipboard"); }
    catch (e) { toast.error("Copy failed - select and copy manually"); }
  };

  const [sharePw, setSharePw] = useState("");
  const [linksOpen, setLinksOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const loadLinks = async () => {
    setLinksLoading(true);
    try { const { data } = await api.get(`/commodities/proposal-pack/shares`); setLinks(data || []); }
    catch (e) { toast.error("Could not load your shared links"); }
    finally { setLinksLoading(false); }
  };
  const revokeLink = async (tok) => {
    try { await api.delete(`/commodities/proposal-pack/share/${tok}`); setLinks((l) => l.filter((x) => x.token !== tok)); toast.success("Client access revoked"); }
    catch (e) { toast.error("Could not revoke the link"); }
  };
  const copyText = (t) => { try { navigator.clipboard.writeText(t); toast.success("Link copied"); } catch (e) {} };

  const [templates, setTemplates] = useState([]);
  const loadTemplates = () => api.get("/me/proposal-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);
  const applyTemplate = (t) => { if (t) setPackForm((p) => ({ ...p, title: t.title || "", blurb: t.blurb || "", client_logo: t.client_logo || "" })); };
  const saveTemplate = async () => {
    const name = (packForm.title || "").trim();
    if (!name) { toast.error("Add a cover title first - it names the template"); return; }
    try {
      const { data } = await api.post(`/me/proposal-templates`, { name, title: packForm.title, blurb: packForm.blurb, client_logo: packForm.client_logo });
      setTemplates(data || []);
      toast.success(`Template "${name}" saved`);
    } catch (e) { toast.error("Could not save template"); }
  };

  const [recentPacks, setRecentPacks] = useState([]);
  const loadRecent = () => api.get("/me/recent-packs").then((r) => setRecentPacks(r.data || [])).catch(() => {});
  useEffect(() => { loadRecent(); }, []);
  const reDownload = async (r) => {
    try {
      const res = await api.post(`/commodities/proposal-pack`, { commodities: r.commodities, title: r.title, client_name: r.client_name, blurb: r.blurb }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(r.title || "SYMIN_Proposal_Pack").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Proposal pack re-downloaded");
      loadRecent();
    } catch (e) { toast.error("Could not re-download the pack"); }
  };

  const [mgrOpen, setMgrOpen] = useState(false);
  const [mgrEdit, setMgrEdit] = useState({ id: null, name: "" });
  const renameTemplate = async (t, name) => {
    if (!name || !name.trim()) { toast.error("Enter a name"); return; }
    try { const { data } = await api.patch(`/me/proposal-templates/${t.id}`, { name: name.trim() }); setTemplates(data || []); toast.success("Template renamed"); }
    catch (e) { toast.error("Could not rename"); }
  };
  const deleteTemplate = async (t) => {
    try { const { data } = await api.delete(`/me/proposal-templates/${t.id}`); setTemplates(data || []); toast.info(`Template "${t.name}" removed`); }
    catch (e) { toast.error("Could not delete"); }
  };

  useEffect(() => {
    api.get("/catalog").then((r) => {
      setCat(r.data);
      const groups = r.data.commodity_groups || {};
      const first = Object.values(groups)[0]?.[0];
      setSel(first || Object.keys(r.data.commodity_profiles || {})[0] || null);
    }).catch(() => setCat({ commodity_profiles: {}, commodity_groups: {} }));
  }, []);

  if (!cat) return <div className="p-8 text-slate-400 text-sm" data-testid="explorer-loading">Loading commodity models…</div>;

  const profiles = cat.commodity_profiles || {};
  const groups = cat.commodity_groups || {};
  const bdefs = cat.band_index_defs || {};
  const prof = sel ? profiles[sel] : null;

  const chip = (t, i) => (
    <span key={i} className="inline-block rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-1 text-xs">{t}</span>
  );

  const Section = ({ icon: Icon, title, children, testid }) => (
    <div className="mb-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2.5">
        <Icon size={15} className="text-orange-500" />
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</h4>
      </div>
      {children}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="commodity-explorer">
      <div>
        <button onClick={() => nav("/console")} data-testid="explorer-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-1"><ArrowLeft size={15} /> Back to Console</button>
        <p className="text-[12px] font-semibold tracking-[0.2em] uppercase text-orange-500">The Science Behind Every Target</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Commodity Model Explorer</h1>
        <p className="text-slate-500 max-w-3xl mt-2 text-sm">For each commodity, SYMIN applies a dedicated targeting model - the diagnostic spectral bands, alteration and structural indicators, prospectivity weights and colour palette used to rank exploration targets.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-4" data-testid="explorer-list">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="proposal-pack-bar">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Proposal Pack</div>
            <p className="text-[11px] text-slate-400 mb-2">Tick commodities, then bundle their science one-pagers into a single branded PDF.</p>
            <button onClick={() => setPackOpen(true)} disabled={!packSel.length} data-testid="build-proposal-pack"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 text-white px-3 py-2 text-sm font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50">
              <FileDown size={14} /> Build pack{packSel.length ? ` (${packSel.length})` : ""}
            </button>
            <button onClick={() => { setLinksOpen(true); loadLinks(); }} data-testid="open-links-manager" className="mt-2 w-full text-[11px] text-slate-500 hover:text-orange-600 flex items-center justify-center gap-1"><Link2 size={12} /> Shared links</button>
            {templates.length > 0 && (
              <button onClick={() => setMgrOpen(true)} data-testid="open-template-manager" className="mt-2 w-full text-[11px] text-slate-500 hover:text-orange-600 flex items-center justify-center gap-1"><Settings2 size={12} /> Manage templates ({templates.length})</button>
            )}

            {recentPacks.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200" data-testid="recent-packs">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><History size={12} /> Recent Packs</div>
                <div className="space-y-1.5">
                  {recentPacks.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5" data-testid={`recent-pack-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-700 truncate">{r.title || "Proposal Pack"}</div>
                        <div className="text-[10px] text-slate-400 truncate">{(r.commodities || []).join(", ")}{r.client_name ? ` · ${r.client_name}` : ""}</div>
                      </div>
                      <button onClick={() => reDownload(r)} title="Re-download this pack" data-testid={`recent-pack-dl-${r.id}`} className="shrink-0 text-orange-500 hover:text-orange-600"><FileDown size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Dialog open={packOpen} onOpenChange={(o) => { setPackOpen(o); if (!o) { loadRecent(); setShareLink(null); setSharePw(""); } }}>
              <DialogContent data-testid="pack-dialog">
                <DialogHeader>
                  <DialogTitle>Proposal Pack - Cover Options</DialogTitle>
                  <DialogDescription>Customize the branded cover, then download a single PDF with the {packSel.length} selected model{packSel.length === 1 ? "" : "s"}.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  {templates.length > 0 && (
                    <div data-testid="pack-templates">
                      <Label className="text-xs">Load a saved template</Label>
                      <select className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="pack-template-select"
                        onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); applyTemplate(t); }} defaultValue="">
                        <option value="" disabled>Choose a template…</option>
                        {templates.map((t) => <option key={t.id} value={t.id} label={t.name} />)}
                      </select>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Cover title</Label>
                    <Input value={packForm.title} onChange={(e) => setPackForm({ ...packForm, title: e.target.value })} placeholder="Exploration Proposal Pack" className="mt-1" data-testid="pack-title" />
                  </div>
                  <div>
                    <Label className="text-xs">Client name</Label>
                    <Input value={packForm.client_name} onChange={(e) => setPackForm({ ...packForm, client_name: e.target.value })} placeholder="e.g. Acme Mining Ltd" className="mt-1" data-testid="pack-client" />
                  </div>
                  <div>
                    <Label className="text-xs">Cover blurb</Label>
                    <Textarea value={packForm.blurb} onChange={(e) => setPackForm({ ...packForm, blurb: e.target.value })} placeholder="A short introductory paragraph for the cover page." rows={3} className="mt-1" data-testid="pack-blurb" />
                  </div>
                  <div>
                    <Label className="text-xs">Client logo (optional)</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input type="file" accept="image/*" onChange={onLogoFile} className="text-xs" data-testid="pack-logo" />
                      {packForm.client_logo && <img src={packForm.client_logo} alt="logo" className="h-8 w-auto rounded border border-slate-200" />}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="pack-share-block">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1"><Link2 size={12} /> Shareable client link</div>
                    <p className="text-[11px] text-slate-400 mb-2">Publish a read-only web link your client can open in any browser - no login, expires automatically.</p>
                    <div className="flex items-center gap-2">
                      <select value={shareExpiry} onChange={(e) => setShareExpiry(Number(e.target.value))} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="pack-share-expiry">
                        <option value={7}>Expires in 7 days</option>
                        <option value={30}>Expires in 30 days</option>
                        <option value={90}>Expires in 90 days</option>
                        <option value={0}>Never expires</option>
                      </select>
                      <Button variant="outline" onClick={createShareLink} disabled={sharing} className="h-9" data-testid="pack-create-link">
                        {sharing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Link2 size={14} className="mr-1.5" />} Create link
                      </Button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Lock size={13} className="text-slate-400 shrink-0" />
                      <Input type="password" value={sharePw} onChange={(e) => setSharePw(e.target.value)} placeholder="Optional passcode (protect this link)" className="h-8 text-xs" data-testid="pack-share-password" />
                    </div>
                    {shareLink && (
                      <div className="mt-2.5" data-testid="pack-share-result">
                        <div className="flex items-center gap-1.5">
                          <Input readOnly value={shareLink.url} onFocus={(e) => e.target.select()} className="h-8 text-xs" data-testid="pack-share-url" />
                          <button onClick={copyShareLink} title="Copy link" data-testid="pack-share-copy" className="shrink-0 rounded-md border border-slate-200 bg-white p-2 text-slate-500 hover:text-orange-600"><Copy size={13} /></button>
                          <a href={shareLink.url} target="_blank" rel="noopener noreferrer" title="Open link" data-testid="pack-share-open-link" className="shrink-0 rounded-md border border-slate-200 bg-white p-2 text-slate-500 hover:text-orange-600"><ExternalLink size={13} /></a>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1">
                          {shareLink.protected && <span className="text-red-600 font-medium inline-flex items-center gap-1 mr-2"><Lock size={10} /> Passcode protected</span>}
                          {shareLink.expires_at ? `Expires ${new Date(shareLink.expires_at).toLocaleDateString()}` : "This link never expires - revoke it any time from Shared links."}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={saveTemplate} className="mr-auto" data-testid="pack-save-template">Save as template</Button>
                  <Button variant="outline" onClick={() => setPackOpen(false)} data-testid="pack-cancel">Cancel</Button>
                  <Button onClick={doBuildPack} disabled={packing} data-testid="pack-download">
                    {packing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileDown size={14} className="mr-1.5" />} Download pack
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={mgrOpen} onOpenChange={setMgrOpen}>
              <DialogContent data-testid="template-manager">
                <DialogHeader>
                  <DialogTitle>Proposal Templates</DialogTitle>
                  <DialogDescription>Rename or delete your saved cover templates.</DialogDescription>
                </DialogHeader>
                <div className="max-h-80 overflow-y-auto -mx-1 px-1">
                  {templates.length ? templates.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 justify-between border-b border-slate-100 py-2" data-testid={`tpl-row-${t.id}`}>
                      {mgrEdit.id === t.id ? (
                        <div className="flex items-center gap-1.5 flex-1">
                          <Input value={mgrEdit.name} onChange={(e) => setMgrEdit({ id: t.id, name: e.target.value })} className="h-8" data-testid={`tpl-name-input-${t.id}`} />
                          <Button size="sm" className="h-8" onClick={async () => { await renameTemplate(t, mgrEdit.name); setMgrEdit({ id: null, name: "" }); }} data-testid={`tpl-save-${t.id}`}>Save</Button>
                        </div>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{t.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{t.title || "No cover title"}</div>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 shrink-0">
                        {mgrEdit.id !== t.id && <button className="text-slate-400 hover:text-slate-700" onClick={() => setMgrEdit({ id: t.id, name: t.name })} data-testid={`tpl-rename-${t.id}`}><Pencil size={13} /></button>}
                        <button className="text-slate-400 hover:text-red-500" onClick={() => deleteTemplate(t)} data-testid={`tpl-delete-${t.id}`}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  )) : <p className="text-sm text-slate-400 py-3" data-testid="tpl-empty">No saved templates yet.</p>}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={linksOpen} onOpenChange={setLinksOpen}>
              <DialogContent data-testid="links-manager" className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Shared Links</DialogTitle>
                  <DialogDescription>Manage client access - see when and how often each proposal was opened, copy or open a link, and revoke access instantly.</DialogDescription>
                </DialogHeader>
                <div className="max-h-[26rem] overflow-y-auto -mx-1 px-1 space-y-2">
                  {linksLoading ? (
                    <div className="py-6 text-center text-slate-400 text-sm"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
                  ) : links.length ? links.map((l) => {
                    const url = `${window.location.origin}${l.path}`;
                    return (
                      <div key={l.token} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`link-row-${l.token}`}>
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
                              {l.protected && <Lock size={12} className="text-red-500 shrink-0" />}{l.title || "Proposal Pack"}
                            </div>
                            <div className="text-[11px] text-slate-400 truncate">{(l.commodities || []).join(", ")}{l.client_name ? ` · ${l.client_name}` : ""}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => copyText(url)} title="Copy link" data-testid={`link-copy-${l.token}`} className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:text-orange-600"><Copy size={13} /></button>
                            <a href={url} target="_blank" rel="noopener noreferrer" title="Open link" data-testid={`link-open-${l.token}`} className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:text-orange-600"><ExternalLink size={13} /></a>
                            <button onClick={() => revokeLink(l.token)} title="Revoke access" data-testid={`link-revoke-${l.token}`} className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:text-red-500"><Trash2 size={13} /></button>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                          <span className="inline-flex items-center gap-1 font-semibold text-slate-600" data-testid={`link-views-${l.token}`}><Eye size={12} /> {l.views} view{l.views === 1 ? "" : "s"}</span>
                          {l.last_viewed && <span className="text-slate-400">Last opened {fmtWhen(l.last_viewed)}</span>}
                          {l.expired ? <span className="text-red-500 font-medium">Expired</span>
                            : l.expires_at ? <span className="text-slate-400">Expires {new Date(l.expires_at).toLocaleDateString()}</span>
                            : <span className="text-slate-400">No expiry</span>}
                        </div>
                        {(l.recent_opens || []).length > 0 && (
                          <details className="mt-1.5" data-testid={`link-analytics-${l.token}`}>
                            <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-slate-600">Open history ({l.recent_opens.length})</summary>
                            <ul className="mt-1 space-y-0.5">
                              {l.recent_opens.map((t, i) => <li key={i} className="text-[11px] text-slate-500">• {fmtWhen(t)}</li>)}
                            </ul>
                          </details>
                        )}
                      </div>
                    );
                  }) : <p className="text-sm text-slate-400 py-6 text-center" data-testid="links-empty">No shared links yet. Create one from the Proposal Pack dialog.</p>}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {Object.entries(groups).map(([g, names]) => (
            <div key={g}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">{g}</div>
              <div className="space-y-1">
                {(names || []).filter((n) => profiles[n]).map((n) => (
                  <div key={n} className={`flex items-center gap-2 pl-2 pr-1 rounded-lg border transition-colors ${sel === n ? "bg-slate-900 border-slate-900" : "bg-white border-slate-200 hover:border-orange-300"}`}>
                    <input type="checkbox" className="h-3.5 w-3.5 accent-orange-500 shrink-0" checked={packSel.includes(n)} onChange={() => togglePack(n)} data-testid={`pack-check-${n}`} />
                    <button data-testid={`explorer-commodity-${n}`} onClick={() => setSel(n)}
                      className={`flex-1 text-left py-2 text-sm ${sel === n ? "text-white" : "text-slate-700"}`}>
                      {profiles[n].label || n}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {prof ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="explorer-detail">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-orange-500">{prof.group}</div>
                <h2 className="text-2xl font-bold text-slate-900">{prof.label}</h2>
              </div>
              {prof.legend_label && (
                <span className="text-[11px] text-slate-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 max-w-xs" data-testid="explorer-legend">{prof.legend_label}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3 mb-1" data-testid="explorer-actions">
              <button onClick={() => nav(`/remote-sensing?new=1&commodity=${encodeURIComponent(sel)}`)} data-testid="explorer-load-model"
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-3.5 py-2 text-sm font-semibold hover:bg-orange-500 transition-colors">
                <Rocket size={15} /> Load this model
              </button>
              <button onClick={exportPdf} disabled={exporting} data-testid="explorer-export-pdf"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 text-slate-700 px-3.5 py-2 text-sm font-semibold hover:border-orange-300 hover:text-orange-600 transition-colors disabled:opacity-60">
                {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Export one-pager PDF
              </button>
            </div>
            {prof.report_intro && <p className="text-sm text-slate-600 leading-relaxed mb-6 mt-2">{prof.report_intro}</p>}

            <Section icon={Layers} title="Diagnostic Spectral Bands & Indices" testid="explorer-indices">
              <div className="space-y-2">
                {(prof.indices || []).map((k) => {
                  const d = bdefs[k] || {};
                  const w = (prof.weights || {})[k];
                  return (
                    <div key={k} className="flex items-start gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`explorer-index-${k}`}>
                      <div className="w-32 shrink-0">
                        <div className="text-sm font-semibold text-slate-800">{d.label || k}</div>
                        <div className="text-[11px] font-mono text-orange-600">{d.ratio || ""}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-500">{d.desc || ""}</div>
                      </div>
                      {w != null && (
                        <div className="shrink-0 text-right">
                          <div className="text-sm font-bold text-slate-900 tabular-nums">{w}</div>
                          <div className="text-[10px] text-slate-400 uppercase">weight</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {prof.key_bands && <p className="text-xs text-slate-500 mt-2.5"><span className="font-semibold text-slate-600">Key bands: </span>{prof.key_bands}</p>}
            </Section>

            <div className="grid sm:grid-cols-2 gap-x-8">
              <Section icon={Sparkles} title="Alteration Indicators" testid="explorer-alteration">
                <div className="flex flex-wrap gap-1.5">{(prof.alteration_indicators || []).map(chip)}</div>
              </Section>
              <Section icon={Target} title="Structural Controls" testid="explorer-structural">
                <div className="flex flex-wrap gap-1.5">{(prof.structural_controls || []).map(chip)}</div>
              </Section>
              <Section icon={Mountain} title="Lithological / Geomorphic Controls" testid="explorer-litho">
                <div className="flex flex-wrap gap-1.5">{(prof.litho_geomorph_controls || []).map(chip)}</div>
              </Section>
              <Section icon={Gem} title="Deposit Models" testid="explorer-deposits">
                <div className="flex flex-wrap gap-1.5">{(prof.deposit_models || []).map(chip)}</div>
              </Section>
            </div>

            <Section icon={Palette} title="Ranking Logic & Palette" testid="explorer-ranking">
              {prof.ranking_criteria && <p className="text-sm text-slate-600 mb-3">{prof.ranking_criteria}</p>}
              <div className="flex flex-wrap items-center gap-4 text-xs">
                {prof.priority_thresholds && (
                  <div className="flex items-center gap-2" data-testid="explorer-thresholds">
                    <span className="font-semibold text-slate-500">Priority thresholds:</span>
                    <span className="rounded bg-orange-500 text-white px-2 py-0.5 font-bold">P1 ≥ {prof.priority_thresholds.p1}</span>
                    <span className="rounded bg-slate-400 text-white px-2 py-0.5 font-bold">P2 ≥ {prof.priority_thresholds.p2}</span>
                    <span className="rounded bg-slate-200 text-slate-700 px-2 py-0.5 font-bold">min {prof.priority_thresholds.min}</span>
                  </div>
                )}
                {prof.palette && <div className="flex items-center gap-1.5"><span className="font-semibold text-slate-500">Palette:</span> <span className="font-mono text-slate-700">{prof.palette}</span></div>}
              </div>
            </Section>

            {prof.sensors && (
              <Section icon={Satellite} title="Sensors Used" testid="explorer-sensors">
                <div className="flex flex-wrap gap-1.5">{prof.sensors.map(chip)}</div>
              </Section>
            )}

            <p className="text-[11px] text-slate-400 border-t border-slate-100 pt-4 mt-2">All outputs are remote-sensing exploration targets requiring field verification - never confirmed mineral deposits.</p>
          </div>
        ) : (
          <div className="text-slate-400 text-sm">Select a commodity to view its targeting model.</div>
        )}
      </div>
    </div>
  );
}
